import { useQuery } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';

/**
 * Catálogo de visitas y traslados, ahora sobre tablas.
 *
 * Antes eran tres JSON guardados en el modelo Parametro
 * (precios_conceptos_visita, tipos_instalacion_visita y
 * descuento_traslado_fibrapoints). El backend los movió a tablas
 * (CatalogoConcepto con FK real a productos, CatalogoMetodoPago,
 * CatalogoTipoInstalacion, CatalogoConfigVisitas) y este endpoint entrega y
 * recibe la MISMA forma que tenían esos JSON, así que los hooks que lo
 * consumen (usePreciosConceptosVisita, useTiposInstalacion,
 * useDescuentoTrasladoFibra) conservan su contrato y ninguna pantalla cambió.
 */

const { get, put } = erpApi();

const URL_CATALOGO = `${urlBackend}api/v1/catalogo-visitas-traslados/`;

export const QK_CATALOGO_VISITAS_TRASLADOS = ['catalogo-visitas-traslados'];

/** La misma forma que tenía el `valor` del Parametro precios_conceptos_visita. */
export type PreciosConceptosJson = {
  iva_porcentaje: number;
  conceptos: unknown[];
  punto_red: { precio_metro_sin_iva: number; metros_libres: number };
  punto_red_traslado: { precio_metro_sin_iva: number; metros_libres: number };
  metodos_pago: string[];
  metodos_pago_traslado: string[];
  excedente_fibra_traslado: { metros_gratis: number; precio_metro_sin_iva: number };
};

export type CatalogoVisitasTraslados = {
  precios_conceptos: PreciosConceptosJson;
  tipos_instalacion: { tipos: unknown[] };
  descuento_traslado_fibrapoints: number;
  advertencias?: string[];
};

/** El catálogo completo, leído de las tablas. UNA sola petición para los tres hooks. */
export function useFetchCatalogoVisitasTraslados() {
  return useQuery({
    queryKey: QK_CATALOGO_VISITAS_TRASLADOS,
    queryFn: async () => {
      const res = await get<CatalogoVisitasTraslados>(URL_CATALOGO, true);
      return res.data;
    },
    refetchOnWindowFocus: false,
  });
}

/**
 * Guarda una o varias secciones del catálogo (lo usa Catálogos ›
 * Administración). Acepta un subconjunto de las tres claves; el backend
 * upserta por nombre y desactiva lo que ya no viene, todo en una transacción.
 * Devuelve el catálogo releído más las advertencias (p. ej. productos ligados
 * que ya no existen en inventario).
 */
export async function guardarCatalogoVisitasTraslados(
  parcial: Partial<CatalogoVisitasTraslados>,
): Promise<CatalogoVisitasTraslados> {
  const res = await put<CatalogoVisitasTraslados>(URL_CATALOGO, parcial as any, true);
  if ((res as any).status === 'error') {
    throw new Error((res as any).message || 'No se pudo guardar el catálogo.');
  }
  return res.data;
}
